import type { Session } from '@supabase/supabase-js';
import { Platform } from 'react-native';
import { APP_RELEASE } from './app-release';
import { getClientEventContext } from './client-event-context';
import {
  sendClientObservability,
  updateBetterStackRoute,
  updateBetterStackUser,
} from './observability';

export type AnalyticsScalar = string | number | boolean | null;

export type AnalyticsEventInput = {
  eventName: string;
  route?: string | null;
  screen?: string | null;
  component?: string | null;
  message?: string | null;
  stepNumber?: number | null;
  stepLabel?: string | null;
  metadata?: Record<string, AnalyticsScalar> | null;
};

type BetterStackBrowserFn = {
  (...args: unknown[]): void;
  q?: unknown[][];
  l?: number;
};

type BrowserWindow = Window & {
  betterstack?: BetterStackBrowserFn;
  __perdiemBetterStackBrowserTagInitialized?: boolean;
  __perdiemBetterStackBrowserTagLoadScheduled?: boolean;
  requestIdleCallback?: (
    callback: () => void,
    options?: { timeout?: number },
  ) => number;
};

const APP_ENVIRONMENT =
  process.env['EXPO_PUBLIC_APP_ENV'] ?? process.env['NODE_ENV'] ?? 'development';
const DEFAULT_BETTER_STACK_JS_TAG_TOKEN = '9zuxpa3uDzyqkiBMfB8Wqzrf';
const BETTER_STACK_JS_TAG_TOKEN =
  process.env['EXPO_PUBLIC_BETTER_STACK_JS_TAG_TOKEN'] ??
  DEFAULT_BETTER_STACK_JS_TAG_TOKEN;
const ENABLE_JS_TAG_IN_DEV =
  process.env['EXPO_PUBLIC_ENABLE_BETTER_STACK_JS_TAG_IN_DEV'] === 'true';

function getBrowserWindow(): BrowserWindow | null {
  if (Platform.OS !== 'web') return null;
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  return window as BrowserWindow;
}

function shouldLoadBetterStackBrowserTag(): boolean {
  return Boolean(
    Platform.OS === 'web' &&
      BETTER_STACK_JS_TAG_TOKEN &&
      (!__DEV__ || ENABLE_JS_TAG_IN_DEV),
  );
}

export function initializeAnalytics() {
  if (!shouldLoadBetterStackBrowserTag()) return;

  const browserWindow = getBrowserWindow();
  if (!browserWindow) return;
  if (browserWindow.__perdiemBetterStackBrowserTagInitialized) return;

  const betterstack: BetterStackBrowserFn =
    browserWindow.betterstack ??
    ((...args: unknown[]) => {
      const queued = betterstack.q ?? [];
      queued.push(args);
      betterstack.q = queued;
    });

  if (!browserWindow.betterstack) {
    betterstack.l = Date.now();
    browserWindow.betterstack = betterstack;
  }

  browserWindow.betterstack('init', {
    environment: APP_ENVIRONMENT,
    release: APP_RELEASE,
    autoPageview: true,
  });

  const scriptSrc = `https://betterstack.net/b.js?t=${encodeURIComponent(
    BETTER_STACK_JS_TAG_TOKEN ?? '',
  )}`;
  const loadScript = () => {
    if (document.querySelector(`script[src="${scriptSrc}"]`)) return;
    const script = document.createElement('script');
    script.src = scriptSrc;
    script.async = true;
    script.crossOrigin = 'anonymous';
    document.head.appendChild(script);
  };

  if (!browserWindow.__perdiemBetterStackBrowserTagLoadScheduled) {
    browserWindow.__perdiemBetterStackBrowserTagLoadScheduled = true;
    if (typeof browserWindow.requestIdleCallback === 'function') {
      browserWindow.requestIdleCallback(loadScript, { timeout: 3500 });
    } else {
      window.setTimeout(loadScript, 2500);
    }
  }

  browserWindow.__perdiemBetterStackBrowserTagInitialized = true;
}

function cleanEventPayload(
  payload: Record<string, AnalyticsScalar | undefined>,
): Record<string, AnalyticsScalar> {
  return Object.fromEntries(
    Object.entries(payload).filter(([, value]) => value !== undefined),
  ) as Record<string, AnalyticsScalar>;
}

async function buildBrowserEventPayload(input: AnalyticsEventInput) {
  const context = await getClientEventContext().catch(() => null);

  return cleanEventPayload({
    route: input.route ?? null,
    screen: input.screen ?? null,
    component: input.component ?? null,
    message: input.message ?? null,
    stepNumber: input.stepNumber ?? null,
    stepLabel: input.stepLabel ?? null,
    platform: Platform.OS,
    environment: APP_ENVIRONMENT,
    release: APP_RELEASE,
    ...(context ?? {}),
    ...(input.metadata ?? {}),
  });
}

function trackBrowserEvent(input: AnalyticsEventInput) {
  if (!shouldLoadBetterStackBrowserTag()) return;

  initializeAnalytics();
  const browserWindow = getBrowserWindow();
  if (!browserWindow?.betterstack) return;

  void buildBrowserEventPayload(input).then((payload) => {
    browserWindow.betterstack?.('track', input.eventName, payload);
  });
}

export function identifyAnalyticsUser(session: Session | null) {
  updateBetterStackUser(session);
  initializeAnalytics();

  const browserWindow = getBrowserWindow();
  if (!browserWindow?.betterstack || !shouldLoadBetterStackBrowserTag()) return;

  if (!session) {
    browserWindow.betterstack('user', null);
    return;
  }

  const user: Record<string, string> = {
    id: session.user.id,
    isDemo: session.user.app_metadata?.['isDemo'] === true ? 'true' : 'false',
  };

  if (session.user.email) {
    user.email = session.user.email;
  }

  const fullName = session.user.user_metadata?.['full_name'];
  if (typeof fullName === 'string' && fullName.trim()) {
    user.username = fullName.trim();
  }

  browserWindow.betterstack('user', user);
}

export function updateAnalyticsRoute(route: string | null | undefined) {
  updateBetterStackRoute(route);
}

export function trackEvent(input: AnalyticsEventInput) {
  trackBrowserEvent(input);

  void sendClientObservability({
    kind: 'event',
    eventName: input.eventName,
    route: input.route,
    screen: input.screen,
    component: input.component,
    message: input.message,
    stepNumber: input.stepNumber,
    stepLabel: input.stepLabel,
    metadata: input.metadata,
  });
}

initializeAnalytics();
